import { observer } from 'mobx-react';
import { type FC, useCallback } from 'react';

import { useInjection } from 'inversify-react';
import { useTheme } from '@mui/material/styles';
import { TextField } from '@mui/material';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import styled from 'styled-components';

import { TYPES } from '@/engine/ioc';
import { TYPES as ImagesTYPES } from '@/engine/app/modules/images/iocTypes';

import type { SelectChangeEvent } from '@mui/material/Select';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type { ViewsService } from '@/engine/app/Views.service';
import type { ViewController } from '@/engine/app/View.controller';

import { ImagesMode, SlicerMode, type SliceType } from '@/engine/app/modules/images/interfaces';
import { XRayEffect } from '@/engine/app/modules/images/ImageStack/ImageStack';

import { useImageModeHandlers } from './useImageModeHandlers';
import { useAIPThickness } from '@/components/imagesModeSwitcher/useAIPThickness.ts';
import { useRelatedInfo } from '@/components/imagesModeSwitcher/useRelatedInfo.ts';

export const ImagesModeSwitcher: FC<{
  isMaximized: boolean | undefined;
  sliceType: SliceType;
}> = observer(({ isMaximized, sliceType }) => {
  const theme = useTheme();

  const imagesService = useInjection<ImagesService>(ImagesTYPES.ImagesService);
  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const viewController = useInjection<ViewController>(TYPES.ViewController);

  const { thickness, setThickness, updateThickness } = useAIPThickness(imagesService, sliceType);

  const imageModeHandlers = useImageModeHandlers(viewsService, viewController);

  const { slicerMode, isLongAxisMode, imagesInfo, centerLines } = useRelatedInfo(
    viewsService,
    imagesService,
    sliceType
  );

  const handleSlicerModeChange = useCallback(
    (event: SelectChangeEvent): void => {
      const selectedMode = event.target.value as SlicerMode;
      imagesService.setSlicerMode(sliceType, selectedMode);
    },
    [imagesService, sliceType]
  );

  const handleImageModeChange = useCallback(
    (event: SelectChangeEvent): void => {
      const selectedMode = event.target.value as ImagesMode;
      imageModeHandlers[selectedMode]?.();
    },
    [imageModeHandlers]
  );

  return (
    <ImagesModeContainer>
      {centerLines.length > 0 && (
        <ImagesModeFormControl $imagesMode={true} $isMaximized={isMaximized} size="small" color="primary">
          <Select
            className="mode-switcher"
            sx={{
              width: '8em',
              marginRight: '0.2em',
              fontWeight: 600,
              textShadow: '0px 0px 3px #000000, 0px 0px 4px #000000',
              color: theme.palette.primary.light,
              fontSize: '0.7em',
              '& svg': { color: theme.palette.primary.light, right: 0 },
              '& fieldset': { borderColor: theme.palette.primary.light, filter: 'opacity(0.5)' },
              '& div.MuiSelect-select.MuiSelect-outlined': {
                padding: '.4em 0em 0.3em .7em',
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
            }}
            data-testid="imagesModeSwitcher"
            value={imagesService.imagesMode}
            onChange={handleImageModeChange}
            size="small"
            color="primary"
          >
            <MenuItem value={ImagesMode.MPR} key={'menu-item-MPR'}>
              MPR
            </MenuItem>
            <MenuItem value={ImagesMode.POLYLINE} key={'menu-item-POLYLINE'}>
              {isLongAxisMode ? 'AXIS' : 'CENTERLINE'}
            </MenuItem>
          </Select>
        </ImagesModeFormControl>
      )}
      <ImagesModeFormControl $isMaximized={isMaximized} $imagesMode={false} size="small" color="primary">
        <Select
          className="mode-switcher"
          sx={{
            width: '8em',
            marginRight: '0.2em',
            fontWeight: 600,
            textShadow: '0px 0px 3px #000000, 0px 0px 4px #000000',
            color: theme.palette.primary.light,
            fontSize: '0.7em',
            '& svg': { color: theme.palette.primary.light, right: 0 },
            '& fieldset': { borderColor: theme.palette.primary.light, filter: 'opacity(0.5)' },
            '& div.MuiSelect-select.MuiSelect-outlined': {
              padding: '.4em 0em 0.3em .7em',
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
          }}
          data-testid="imagesModeSwitcher"
          value={slicerMode}
          onChange={handleSlicerModeChange}
          size="small"
          color="primary"
        >
          <MenuItem value={SlicerMode.SINGLE} key={'menu-item-SINGLE'}>
            SINGLE
          </MenuItem>
          <MenuItem value={'AIP'} key={'menu-item-AIP'}>
            AIP
          </MenuItem>
          <MenuItem value={'FLUORO'} key={'menu-item-FLUORO'}>
            FLUORO
          </MenuItem>
        </Select>
        {imagesInfo.xRaySlicerParams?.xRayEffect === XRayEffect.Addition && (
          <div className="aip-thickness">
            <TextField
              sx={{
                width: 35,
                marginRight: '0.2em',
                '& input': {
                  color: 'white',
                  fontSize: '0.7em',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  padding: '.4em 0em 0.3em .7em',
                  borderRadius: '0.3em'
                },
                '& fieldset': { borderColor: theme.palette.primary.light, filter: 'opacity(0.5)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
              }}
              data-testid="aipThickness"
              size="small"
              color="primary"
              slotProps={{
                input: { inputMode: 'numeric' },
                htmlInput: { pattern: '[0-9]*', maxLength: 3 }
              }}
              value={thickness}
              onChange={(e): void => {
                const newValue = e.target.value;
                if (/^\d{0,3}$/.test(newValue)) {
                  setThickness(+newValue);
                }
              }}
              onBlur={(): void => {
                if (thickness !== imagesInfo.xRaySlicerParams?.xRaySliceThickness) {
                  updateThickness(thickness);
                }
              }}
              onKeyDown={(e): void => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (thickness !== imagesInfo.xRaySlicerParams?.xRaySliceThickness) {
                    updateThickness(thickness);
                  }
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
            <SuffixContainer>mm</SuffixContainer>
          </div>
        )}
      </ImagesModeFormControl>
    </ImagesModeContainer>
  );
});

const ImagesModeContainer = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const ImagesModeFormControl = styled(FormControl)<{ $isMaximized: boolean | undefined; $imagesMode: boolean }>`
  top: ${({ $isMaximized }): string => ($isMaximized ? '1em' : '-0.2em')};
  left: ${({ $isMaximized }): string => ($isMaximized ? '5em' : '2em')};
  display: flex;
  margin: 0.5em;
  width: ${({ $imagesMode }): string => ($imagesMode ? '6em' : '12em')};
  flex-direction: row;
`;

const SuffixContainer = styled.span`
  text-shadow:
    0 0 3px #000000,
    0 0 4px #000000,
    0 0 6px #000000,
    0 0 8px #000000,
    0 0 10px #000000;
  color: white;
  font-size: 0.7em;
  line-height: 2.1em;
`;
